import React from "react";
import { twMerge } from "tailwind-merge";

type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "button"
  | "overline";

type TypographyProps = React.HTMLAttributes<HTMLElement> & {
  variant?: TypographyVariant;
  component?: React.ElementType;
  children: React.ReactNode;
};

const variantClassNames: Record<TypographyVariant, string> = {
  h1: "text-5xl font-bold tracking-tight leading-normal",
  h2: "text-4xl font-bold tracking-tight leading-normal",
  h3: "text-3xl font-semibold tracking-tight leading-normal",
  h4: "text-2xl font-semibold tracking-tight leading-normal",
  h5: "text-xl font-medium tracking-tight leading-normal",
  h6: "text-lg font-medium tracking-tight leading-normal",
  subtitle1: "text-lg font-normal tracking-tight leading-normal",
  subtitle2: "text-md font-medium tracking-tight leading-normal",
  body1: "text-md font-normal leading-normal",
  body2: "text-sm font-normal leading-normal",
  caption: "text-xs font-normal leading-normal",
  button: "text-sm font-medium uppercase leading-normal",
  overline: "text-xs font-medium uppercase tracking-tight leading-normal",
};

const variantElements: Record<TypographyVariant, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  button: "span",
  overline: "span",
};

export const Typography: React.FC<TypographyProps> = ({
  variant = "body1",
  component,
  className = "",
  children,
  ...props
}) => {
  const Component = component || variantElements[variant];
  const variantClassName = variantClassNames[variant];

  return (
    <Component
      className={twMerge(
        "font-sans text-text", // Base styles with Sofia Pro font
        variantClassName,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Typography;
